// src/hooks/useFileProtection.ts
import { useEffect, useCallback, useState } from "react";
import {
  AuthResponse,
  fileProtectionService,
} from "../services/fileProtectionService";

// Функция для декодирования URL
const decodeUrl = (encodedUrl: string): string => {
  try {
    return atob(encodedUrl);
  } catch {
    return "";
  }
};

export const useFileProtection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFileUrl, setCurrentFileUrl] = useState<string | null>(null);

  // Method 1: Simple download using ready URL with token
  const downloadFileWithToken = useCallback(
    (authResponse: AuthResponse) => {
      const { downloadUrl } = authResponse;

      if (downloadUrl) {
        // Simple approach - browser will start download automatically
        window.open(downloadUrl, "_blank");
      } else {
        // Fallback: create download link
        const link = document.createElement("a");
        link.href = downloadUrl || currentFileUrl || "";
        link.setAttribute("download", "");
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      }
    },
    [currentFileUrl],
  );

  // Method 2: POST request with credentials (without token)
  const downloadFileWithCredentials = useCallback(
    async (fileUrl: string, login: string, password: string) => {
      try {
        const response = await fetch("/api/file-access/download-simple", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileUrl: fileUrl,
            login: login,
            password: password,
          }),
        });

        if (!response.ok) {
          throw new Error("Download failed");
        }

        // Get blob and create URL for download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = "download";
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
            contentDisposition,
          );
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
            filename = decodeURIComponent(filename);
          }
        }

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);

        // Clean up URL
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download error:", error);
        throw error;
      }
    },
    [],
  );

  // Method 3: Fetch with token (for more control)
  const downloadFileWithFetch = useCallback(
    async (fileUrl: string, token: string) => {
      try {
        const response = await fetch(
          `/api/file-access/download?fileUrl=${encodeURIComponent(fileUrl)}&token=${token}`,
        );

        if (!response.ok) {
          throw new Error("Download failed");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Get filename
        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = "download";
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
            contentDisposition,
          );
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
            filename = decodeURIComponent(filename);
          }
        }

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download error:", error);
        throw error;
      }
    },
    [],
  );

  // Handle link clicks globally
  const handleLinkClick = useCallback(async (event: Event) => {
    const target = event.target as HTMLAnchorElement;

    // Check if it's a link to a file
    if (target.tagName === "A" && target.href) {
      // Check if it's a protected link (encoded)
      if (target.href.includes("#protected:")) {
        event.preventDefault();

        // Extract encoded URL from href
        const encodedUrl = target.href.split("#protected:")[1];
        const originalUrl = decodeUrl(encodedUrl);

        if (originalUrl) {
          setCurrentFileUrl(originalUrl);
          setIsModalOpen(true);
          setError(null);
        }
        return;
      }

      // Check if it's a direct Azure blob storage URL (fallback for non-hidden links)
      if (target.href.includes("blob.core.windows.net")) {
        event.preventDefault();

        try {
          const { requiresAuth } = await fileProtectionService.checkFileAccess(
            target.href,
          );

          if (requiresAuth) {
            setCurrentFileUrl(target.href);
            setIsModalOpen(true);
            setError(null);
          } else {
            // Open file directly
            window.open(target.href, "_blank");
          }
        } catch (err) {
          console.error("Error checking file access:", err);
          // Fallback to direct access
          window.open(target.href, "_blank");
        }
      }
    }
  }, []);

  // Authenticate and download file
  const handleAuthenticate = useCallback(
    async (login: string, password: string) => {
      if (!currentFileUrl) return;

      setIsLoading(true);
      setError(null);

      try {
        const authResponse = await fileProtectionService.authenticateFileAccess(
          login,
          password,
          currentFileUrl,
        );

        if (authResponse.success && authResponse.accessToken) {
          // Choose one of the download methods:

          // Option A: Simple download (recommended)
          downloadFileWithToken(authResponse);

          // Option B: POST with credentials (uncomment if needed)
          // await downloadFileWithCredentials(currentFileUrl, login, password);

          // Option C: Fetch with token (uncomment if needed)
          // await downloadFileWithFetch(currentFileUrl, authResponse.accessToken);

          setIsModalOpen(false);
          setCurrentFileUrl(null);
        } else {
          setError(authResponse.message || "Authentication failed");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentFileUrl,
      downloadFileWithToken,
      //downloadFileWithCredentials,
      //downloadFileWithFetch,
    ],
  );

  // Complete file download handler (alternative approach)
  const handleFileDownload = useCallback(
    async (fileUrl: string, login: string, password: string) => {
      try {
        setIsLoading(true);
        setError(null);

        // 1. Authentication
        const authResponse = await fetch("/api/file-access/authenticate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileUrl: fileUrl,
            login: login,
            password: password,
          }),
        });

        if (!authResponse.ok) {
          throw new Error("Authentication failed");
        }

        const authData = await authResponse.json();

        // 2. Download (choose one method)
        downloadFileWithToken(authData);
      } catch (error) {
        console.error("Error:", error);
        setError(error instanceof Error ? error.message : "Download failed");
      } finally {
        setIsLoading(false);
      }
    },
    [downloadFileWithToken],
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setCurrentFileUrl(null);
    setError(null);
    setIsLoading(false);
  }, []);

  // Set up global event listener
  useEffect(() => {
    document.addEventListener("click", handleLinkClick);

    return () => {
      document.removeEventListener("click", handleLinkClick);
    };
  }, [handleLinkClick]);

  return {
    isModalOpen,
    isLoading,
    error,
    handleAuthenticate,
    closeModal,
    downloadFileWithToken,
    downloadFileWithCredentials,
    downloadFileWithFetch,
    handleFileDownload,
  };
};
